import { IPublicClientApplication, AccountInfo, SilentRequest, InteractionRequiredAuthError } from '@azure/msal-browser';

export class TokenService {
  private static instance: TokenService | null = null;
  private msalInstance: IPublicClientApplication;
  private tokenCache: Map<string, { token: string, expires: number }> = new Map();
  
  constructor(msalInstance: IPublicClientApplication) {
    this.msalInstance = msalInstance;
  }
  
  static getInstance(msalInstance: IPublicClientApplication): TokenService {
    if (!TokenService.instance) {
      TokenService.instance = new TokenService(msalInstance);
    }
    return TokenService.instance;
  }
  
  // Your existing getToken method (keep as is)
  async getToken(scopes: string[], account: AccountInfo): Promise<string> {
    // Create a cache key from scopes and account ID
    const cacheKey = `${scopes.sort().join(' ')}|${account.homeAccountId}`;
    
    // Check if we have a valid cached token
    const cached = this.tokenCache.get(cacheKey);
    if (cached && cached.expires > Date.now()) {
      console.log('[TokenService] Using cached token for scopes:', scopes);
      return cached.token;
    }
    
    // Otherwise acquire a new token
    try {
      console.log('[TokenService] Acquiring token for scopes:', scopes);
      const response = await this.msalInstance.acquireTokenSilent({
        scopes,
        account
      } as SilentRequest);
      
      // Cache the token with an expiration time
      const expiresIn = (response.expiresOn?.getTime() || Date.now() + 3600 * 1000) - Date.now() - 300 * 1000;
      this.tokenCache.set(cacheKey, {
        token: response.accessToken,
        expires: Date.now() + expiresIn
      });
      
      return response.accessToken;
    } catch (error) {
      console.error('[TokenService] Error acquiring token:', error);
      
      // Token might be in cache from another call
      const cached = this.tokenCache.get(cacheKey);
      if (cached) {
        console.log('[TokenService] Returning cached token as fallback');
        return cached.token;
      }
      
      throw error;
    }
  }

  /**
   * Check if admin consent was recently completed
   */
  wasConsentRecentlyCompleted(tenantId: string): boolean {
    if (!tenantId) return false;
    
    const consentTime = sessionStorage.getItem(`consent_completed_${tenantId}`);
    const justCompleted = sessionStorage.getItem(`consent_just_completed_${tenantId}`);
    
    if (justCompleted) {
      const justCompletedTime = parseInt(justCompleted);
      const timeSinceJustCompleted = Date.now() - justCompletedTime;
      if (timeSinceJustCompleted < 30000) { // Within 30 seconds of just completing
        console.log('[TokenService] Consent just completed, using extended delays');
        return true;
      } else {
        // Clean up the just completed marker
        sessionStorage.removeItem(`consent_just_completed_${tenantId}`);
      }
    }
    
    if (!consentTime) return false;
    
    const timeSinceConsent = Date.now() - parseInt(consentTime);
    const tenMinutes = 10 * 60 * 1000; // 10 minutes
    
    if (timeSinceConsent < tenMinutes) {
      console.log('[TokenService] Recent admin consent detected, will use extended delays');
      return true;
    }
    
    // Clean up old consent markers
    sessionStorage.removeItem(`consent_completed_${tenantId}`);
    return false;
  }

  /**
   * Verify tenant onboarding status using token acquisition
   */
async verifyTenantOnboarding(criticalScope: string, account: AccountInfo): Promise<boolean> {
  const tenantId = account.tenantId;
  
  // Check if we should skip this verification
  if (this.shouldSkipOnboardingCheck(tenantId)) {
    // Return cached result if available
    return this.isSessionOnboarded(tenantId);
  }
  
  try {
    console.log(`[TokenService] Verifying tenant onboarding with scope: ${criticalScope}`);
    
    // Mark that we're currently checking this tenant
    if (tenantId) {
      sessionStorage.setItem(`checking_${tenantId}`, Date.now().toString());
    }
    
    // Check if consent was recently completed - use longer delay
    const wasRecentConsent = this.wasConsentRecentlyCompleted(tenantId);
    const delay = wasRecentConsent ? 5000 : 1000; // 5 seconds if recent consent, 1 second otherwise
    
    console.log(`[TokenService] Waiting ${delay}ms for Azure AD permission propagation...`);
    await new Promise(resolve => setTimeout(resolve, delay));
    
    const response = await this.msalInstance.acquireTokenSilent({
      scopes: [criticalScope],
      account
    } as SilentRequest);
    
    console.log('[TokenService] Tenant onboarding verification successful');
    
    // Cache this verification result for the session
    if (tenantId) {
      sessionStorage.setItem(`onboarded_${tenantId}`, 'true');
      sessionStorage.removeItem(`checking_${tenantId}`);
      // Clear the recent consent marker since verification succeeded
      sessionStorage.removeItem(`consent_completed_${tenantId}`);
    }
    
    return true;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      console.log('[TokenService] Tenant requires onboarding (admin consent needed)');
      
      // Clear any stale session data
      if (tenantId) {
        sessionStorage.removeItem(`onboarded_${tenantId}`);
        sessionStorage.removeItem(`checking_${tenantId}`);
      }
      
      return false;
    }
    
    console.error('[TokenService] Unexpected error during onboarding verification:', error);
    
    // Clear checking flag on error
    if (tenantId) {
      sessionStorage.removeItem(`checking_${tenantId}`);
    }
    
    return false;
  }
}

  /**
   * Verify multiple scopes for admin consent verification
   */
  async verifyMultipleScopes(scopes: string[], account: AccountInfo): Promise<boolean> {
    try {
      console.log('[TokenService] Verifying multiple scopes:', scopes);
      
      // Extended delay for post-consent verification to allow Azure AD propagation
      console.log('[TokenService] Waiting for Azure AD to propagate admin consent...');
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      const response = await this.msalInstance.acquireTokenSilent({
        scopes,
        account
      } as SilentRequest);
      
      console.log('[TokenService] Multiple scopes verification successful');
      
      // Cache the successful verification
      const cacheKey = `verified_${scopes.sort().join('_')}|${account.homeAccountId}`;
      sessionStorage.setItem(cacheKey, 'true');
      
      return true;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.log('[TokenService] Admin consent required for scopes:', scopes);
        return false;
      }
      
      console.error('[TokenService] Error verifying multiple scopes:', error);
      return false;
    }
  }

  /**
   * Mark tenant as onboarded in session storage
   */
  markTenantOnboarded(tenantId: string): void {
    if (!tenantId) return;
    
    console.log('[TokenService] Marking tenant as onboarded for session:', tenantId);
    sessionStorage.setItem(`onboarded_${tenantId}`, 'true');
    
    // Clear any "checking" flags
    sessionStorage.removeItem(`checking_${tenantId}`);
    
    // Also update localStorage for backward compatibility
    /* const onboardedTenants = JSON.parse(localStorage.getItem('onboardedTenants') || '[]');
    if (!onboardedTenants.includes(tenantId)) {
      onboardedTenants.push(tenantId);
      localStorage.setItem('onboardedTenants', JSON.stringify(onboardedTenants));
    } */
  }

  /**
   * Check if tenant has been verified as onboarded in this session
   */
  isSessionOnboarded(tenantId: string): boolean {
    if (!tenantId) return false;
    return sessionStorage.getItem(`onboarded_${tenantId}`) === 'true';
  }

  // ADD this method after isSessionOnboarded():
  /**
   * Check if tenant onboarding verification should be skipped
   * Returns true if recently verified or if in an intermediate state
   */
  shouldSkipOnboardingCheck(tenantId: string): boolean {
    if (!tenantId) return false;
    
    // Check if we recently verified this tenant (within last 30 seconds)
    const recentCheck = sessionStorage.getItem(`checking_${tenantId}`);
    if (recentCheck) {
      const checkTime = parseInt(recentCheck);
      if (Date.now() - checkTime < 30000) {
        console.log('[TokenService] Recently checked this tenant, skipping duplicate check');
        return true;
      }
    }
    
    return false;
  }  

  /**
   * Clear all cached tokens and onboarding status
   */
  clearCache(): void {
    console.log('[TokenService] Clearing token cache and session data');
    this.tokenCache.clear();
    
    // Clear session storage items related to onboarding
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (key.startsWith('onboarded_') || key.startsWith('verified_') || key.startsWith('checking_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  }

  // Keep any other existing methods you have...
}

export default TokenService;